// Safe access to the Chatwoot live-chat SDK.
//
// Chatwoot sets `window.$chatwoot` and dispatches "chatwoot:ready" before its
// bubble and iframe elements are guaranteed to be in the DOM. Calling an SDK
// method during that window throws — `toggleBubbleVisibility` dereferences a
// null bubble holder. Our call sites run inside React effects, so the throw
// propagates to the error boundary and replaces the entire page with the
// "Ceva nu a mers bine" screen. It only reproduces on a cold cache, which is
// exactly the first-time visitor.
//
// Everything here is best-effort by design: live chat is never worth a white
// screen. If the widget is blocked, offline, or slow, calls are dropped quietly.

export type ChatwootSDK = {
  toggleBubbleVisibility: (state: 'show' | 'hide') => void;
  setUser: (
    id: string,
    attrs: { name?: string; email?: string; phone_number?: string; avatar_url?: string },
  ) => void;
  setCustomAttributes: (attrs: Record<string, string>) => void;
  reset: () => void;
};

// How long to keep waiting for the widget before giving up on a queued call.
const READY_TIMEOUT_MS = 15_000;
const RETRY_INTERVAL_MS = 150;

// The elements Chatwoot's own methods reach for. Class names differ across SDK
// versions, so accept any of them.
const WIDGET_SELECTOR = '.woot-widget-holder, .woot--bubble-holder, #cw-bubble-holder';

function getSDK(): ChatwootSDK | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { $chatwoot?: ChatwootSDK }).$chatwoot;
}

function widgetMounted(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector(WIDGET_SELECTOR) !== null;
}

/**
 * Run `fn` against the Chatwoot SDK once it is genuinely usable — the SDK
 * object exists *and* its DOM has been mounted. Retries until the widget shows
 * up, then gives up quietly. Any error thrown by the SDK is swallowed.
 *
 * Returns a cancel function; call it from an effect's cleanup.
 */
export function withChatwoot(fn: (cw: ChatwootSDK) => void): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = Date.now() + READY_TIMEOUT_MS;

  const attempt = () => {
    if (cancelled) return;

    const cw = getSDK();
    if (cw && widgetMounted()) {
      try {
        fn(cw);
      } catch (err) {
        // A third-party failure must never reach the error boundary.
        console.warn('[chatwoot] SDK call failed, ignoring', err);
      }
      return;
    }

    if (Date.now() >= deadline) return; // widget blocked or never loaded
    timer = setTimeout(attempt, RETRY_INTERVAL_MS);
  };

  attempt();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}
